import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React from "react";
import * as FSPR from "src/generated/hooks/form-service-public/requests";
import * as FSR from "src/generated/hooks/form-service/requests";
import type { OrgConfigType } from "src/generated/hooks/org-config/requests";
import { useOrganization } from "src/hooks/useOrganization";
import * as RD from "src/types/remoteData";

const orgConfigQueryKey = (
  organizationId: string | undefined,
  configType: OrgConfigType
) => ["organizationConfig", organizationId, configType] as const;

export async function fetchConfig<
  CONFIG extends FSR.OrgConfig,
  T extends OrgConfigType = OrgConfigType
>(organizationId: string, configType: T): Promise<CONFIG | null> {
  const response = await FSR.getOrganizationConfig({
    path: { organizationId, configType },
    throwOnError: false,
  });

  if (response.error) {
    if (response.response.status === 404) {
      return null;
    }

    throw toError(response.error);
  }

  return response.data.data as CONFIG;
}

export async function postConfig<
  CONFIG extends FSR.OrgConfig,
  T extends OrgConfigType = OrgConfigType
>(organizationId: string, configType: T, values: CONFIG): Promise<void> {
  const body: FSR.UpdateOrgConfigRequest = {
    type: configType,
    config: values,
  } as FSR.UpdateOrgConfigRequest;
  const response = await FSR.updateOrganizationConfig({
    body,
    path: { organizationId },
    throwOnError: false,
  });

  if (response.error) {
    throw toError(response.error);
  }
}

export async function generateUploadUrl(path: string): Promise<string> {
  const response = await FSR.generateUploadUrl({
    body: { path },
    throwOnError: false,
  });

  if (response.error) {
    throw toError(response.error);
  }

  return response.data.data.url;
}

export class OrgServiceError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function fetchOrganizationPath(
  clientId: string
): Promise<{ orgPath: string }> {
  const response = await FSPR.getPublicOrganizationPathByClientId({
    query: { clientId },
    throwOnError: false,
  });

  if (response.error) {
    throw new OrgServiceError(
      response.response.status,
      response.error.error.message
    );
  }

  return response.data.data;
}

export async function createOrganization(
  org: FSR.CreateOrganizationData["body"]
): Promise<{ organizationId: string; clientId: string }> {
  const response = await FSR.createOrganization({
    body: org,
    throwOnError: false,
  });

  if (response.error) {
    throw toError(response.error);
  }

  return {
    organizationId: response.data.data.organizationId,
    clientId: response.data.data.clientId,
  };
}

export function useOrgConfig<
  CONFIG extends FSR.OrgConfig,
  T extends OrgConfigType = OrgConfigType
>(
  configType: T
): {
  remoteData: RD.RemoteData<Error, CONFIG | null>;
  update: (config: CONFIG) => Promise<void>;
} {
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id;
  const queryClient = useQueryClient();
  const queryKey = orgConfigQueryKey(organizationId, configType);

  const query = useQuery<CONFIG | null, Error>({
    queryKey,
    enabled: organizationId !== undefined,
    queryFn: () => {
      if (!organizationId) {
        throw new Error("Missing organization ID");
      }

      return fetchConfig<CONFIG>(organizationId, configType);
    },
  });

  const remoteData = RD.useFromRestResultMemoized(query);

  const mutation = useMutation<void, Error, CONFIG>({
    mutationFn: async (config) => {
      if (!organizationId) {
        throw new Error("Missing organization ID");
      }

      if (_.isEqual(config, remoteData.toNullable())) {
        return;
      }

      await postConfig<CONFIG>(organizationId, configType, config);
    },
    onSuccess: (_data, config) => {
      queryClient.setQueryData(queryKey, config);
    },
  });

  const update = React.useCallback(
    (config: CONFIG) => mutation.mutateAsync(config),
    [mutation]
  );

  return { remoteData, update };
}

function toError(error: FSR.ErrorResponse | Error): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(error.error.message, { cause: error });
}
